import sum from 'lodash/sum';

import { NESTING_WIDTH_ADJUSTMENT_CLASSNAME } from '@/modules/report/charts/Table/styles';
import { Maybe, UserViewFieldEntityTypeEnum } from '@/types/schema';

import { ColumnWidth } from './types';

export const MAX_ROW_HEIGHT = 42;
export const COLLAPSED_TABLE_HEIGHT = 40;
const OPTIONS_WIDTH = 40;
const MOVE_WIDTH = 24;
const SELECTION_WIDTH = 24;
const EXPAND_WIDTH = 32;
const STATUS_WIDTH = 32;
const TAGS_WIDTH = 120;

export const MIN_WIDTHS: ColumnWidth = {
  move: MOVE_WIDTH,
  selection: SELECTION_WIDTH,
  expand: EXPAND_WIDTH,
  status: STATUS_WIDTH,
  title: 300,
  priority: 100,
  dueDate: 100,
  createdOn: 100,
  assignee: 130,
  linkedTo: 250,
  list: 150,
  project: 150,
  timer: 130,
  stage: 110,
  options: OPTIONS_WIDTH,
  [UserViewFieldEntityTypeEnum.Tag]: TAGS_WIDTH,
};

export const DEFAULT_WIDTHS: ColumnWidth = {
  move: MOVE_WIDTH,
  selection: SELECTION_WIDTH,
  expand: EXPAND_WIDTH,
  status: STATUS_WIDTH,
  priority: 100,
  dueDate: 80,
  createdOn: 112,
  assignee: 102,
  linkedTo: 264,
  list: 120,
  project: 120,
  timer: 150,
  stage: 120,
  options: OPTIONS_WIDTH,
  [UserViewFieldEntityTypeEnum.Tag]: TAGS_WIDTH,
};

export const getWidthBasedOnContainer = (
  containerWidth: number,
  visibleKeys?: {
    name: string;
    entityType: Maybe<UserViewFieldEntityTypeEnum> | undefined;
  }[],
) => {
  const visibleColumnsWidthExceptTitle = visibleKeys?.map((key) => {
    if (key.entityType === UserViewFieldEntityTypeEnum.Tag) {
      return DEFAULT_WIDTHS[UserViewFieldEntityTypeEnum.Tag];
    }
    return DEFAULT_WIDTHS[key.name];
  });

  /**
   * (Option/Status/Move/Selection/Expand) width is not included in visibleKeys
   * Visible keys is derived from taskConfig
   * (Option/Status/Move/Selection/Expand) is governed from FE, so including its width in sum explicitly
   */

  const ALWAYS_VISIBLE_COLUMNS_WIDTH =
    OPTIONS_WIDTH + STATUS_WIDTH + MOVE_WIDTH + SELECTION_WIDTH + EXPAND_WIDTH;
  const sumOfWidths = sum(visibleColumnsWidthExceptTitle) + ALWAYS_VISIBLE_COLUMNS_WIDTH;
  const titleWidth = containerWidth - sumOfWidths;

  return {
    ...DEFAULT_WIDTHS,
    // to avoid the title column width to be less than min width (title column width can go -ve when many columns are added)
    title: titleWidth > MIN_WIDTHS['title'] ? titleWidth : MIN_WIDTHS['title'],
  };
};

export const TASK_LIST_SIZE_CONFIG = {
  default: {
    GroupByHeader: { variant: 'heading-s' },
    Move: { className: 'move-cell !px-0' },
    Selection: { className: '!px-0' },
    Expand: { className: '!px-0' },
    Title: {
      variant: 'body-short-02',
      className: NESTING_WIDTH_ADJUSTMENT_CLASSNAME,
      headerCellClassName: '-ms-64 me-64',
    },
    Status: { size: 18, className: '!px-0' },
    Actions: { variant: 'body-short-02' },
    Row: { className: 'pe-24 h-40' },
    ExtraWidthForActions: 32,
  },
} as const;
